import { useState } from 'react';
import { CirclePlusIcon } from 'lucide-react';
import { useSuspenseQuery } from '@apollo/client/react';

import { withSuspense } from 'src/hoc';
import { BeneficiariesDocument, type IBeneficiariesQuery } from 'src/graphql/actions/beneficiary/beneficiaries.query';
import { useOnboardingStepSubmit, useStepSubmitHandler } from 'src/context/onboarding-step-submit';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectSeparator, SelectTrigger, SelectValue } from '@/components/ui/select';
import { BeneficiariesOrderByEnum, QueryOrderEnum } from 'src/graphql/schema';

import { useBeneficiaryContext, useSaveBeneficiary } from '../model';

const ADD_NEW_BENEFICIARY = 'ADD_NEW_BENEFICIARY';
const SELECT_ID = 'beneficiary-select';
const DESCRIPTION_ID = 'beneficiary-select-description';

type IBeneficiaryOption = IBeneficiariesQuery['beneficiaries']['nodes'][number];

const BeneficiaryPicker = () => {
  const { beneficiary, setMode } = useBeneficiaryContext();
  const { clearStepError } = useOnboardingStepSubmit();
  const { data } = useSuspenseQuery(BeneficiariesDocument, {
    variables: { perPage: 100, order: QueryOrderEnum.Asc, orderBy: BeneficiariesOrderByEnum.CompanyName },
  });
  const saveBeneficiary = useSaveBeneficiary();
  const [isSelectionMissing, setIsSelectionMissing] = useState(false);

  const organizations = data.beneficiaries.nodes;

  const requireBeneficiary = async () => {
    if (beneficiary) return true;

    setIsSelectionMissing(true);

    return 'Select a beneficiary to continue.';
  };

  useStepSubmitHandler(requireBeneficiary);

  const saveBeneficiaryFromOrganization = async (organization: IBeneficiaryOption) => {
    if (organization.id === beneficiary?.id) {
      setMode('view');
      return;
    }

    const beneficiaryParams = {
      companyName: organization.companyName ?? '',
      companyWebsite: organization.companyWebsite,
      companyRegistrationNumber: organization.companyRegistrationNumber,
      street: organization.street ?? '',
      streetNumber: organization.streetNumber,
      city: organization.city ?? '',
      postalCode: organization.postalCode ?? '',
      region: organization.region,
      country: organization.country,
    };

    await saveBeneficiary(beneficiaryParams);
  };

  const pickOption = (value: string) => {
    setIsSelectionMissing(false);
    clearStepError();

    if (value === ADD_NEW_BENEFICIARY) {
      setMode('create');
      return;
    }

    const organization = organizations.find(({ id }) => id === value);

    if (organization) void saveBeneficiaryFromOrganization(organization);
  };

  return (
    <div className="w-full">
      <Label htmlFor={SELECT_ID}>Choose a beneficiary</Label>
      <Select value={beneficiary?.id ?? ''} onValueChange={pickOption}>
        <SelectTrigger
          id={SELECT_ID}
          aria-describedby={DESCRIPTION_ID}
          aria-invalid={isSelectionMissing}
          className="w-full"
        >
          <SelectValue placeholder="Select a beneficiary" />
        </SelectTrigger>
        <SelectContent>
          {organizations.map(({ id, companyName }) => (
            <SelectItem key={id} value={id}>
              {companyName}
            </SelectItem>
          ))}
          {organizations.length > 0 && <SelectSeparator />}
          <SelectItem value={ADD_NEW_BENEFICIARY} className="text-sky-700 font-semibold">
            <CirclePlusIcon className="size-3.5 text-sky-700" /> Add new beneficiary
          </SelectItem>
        </SelectContent>
      </Select>
      {isSelectionMissing ? (
        <p id={DESCRIPTION_ID} className="mt-2 text-2xs font-medium text-destructive">
          Choose a beneficiary or add a new one.
        </p>
      ) : (
        <p id={DESCRIPTION_ID} className="mt-2 text-sm text-muted-foreground">
          You can choose any beneficiary linked to this application, or any organization linked through an accepted
          vendor request.
        </p>
      )}
    </div>
  );
};

const BeneficiaryPickerSkeleton = () => <Skeleton className="h-8 w-full rounded-lg" />;

export default withSuspense(BeneficiaryPicker, BeneficiaryPickerSkeleton);
